import React, { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RefreshCw, TrendingUp, Users, BarChart3 } from 'lucide-react';
import { useChatMutation } from '@/api/chatApi';
import type { RootState } from '@/app/store';
import {
  addMessage,
  setSessionId,
  resetChat,
  setLoading,
  setError,
  clearError,
} from './chatSlice';
import { ChatMessage } from '@/components/chat/ChatMessage';
import { ChatInput } from '@/components/chat/ChatInput';
import { ChartRenderer } from '@/components/data/ChartRenderer';
import { TableRenderer } from '@/components/data/TableRenderer';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Button } from '@/components/ui/Button';
import { generateId } from '@/lib/utils';
import { Logo } from '@/components/Logo';

export const ChatView: React.FC = () => {
  const dispatch = useDispatch();
  const { messages, session_id, isLoading, error } = useSelector(
    (state: RootState) => state.chat
  );
  const [chat] = useChatMutation();
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  const handleSend = async (input: string) => {
    if (!input.trim() || isLoading) return;

    dispatch(clearError());
    dispatch(setLoading(true));

    // Add user message
    const userMessage = {
      id: generateId(),
      sender: 'user' as const,
      text: input,
      timestamp: new Date(),
    };
    dispatch(addMessage(userMessage));

    try {
      const response = await chat({ input, session_id }).unwrap();
      
      // Update session ID if provided
      if (response.session_id) {
        dispatch(setSessionId(response.session_id));
      }

      // Add AI response
      const aiMessage = {
        id: generateId(),
        sender: 'ai' as const,
        text: response.explanation,
        data: response,
        timestamp: new Date(),
      };
      dispatch(addMessage(aiMessage));
    } catch (err: any) {
      console.error('Chat error:', err);
      // If the error contains a friendly explanation, show it as an AI message
      const explanation = err?.data?.explanation || err?.data?.message || err?.message;
      if (explanation) {
        const aiMessage = {
          id: generateId(),
          sender: 'ai' as const,
          text: explanation,
          timestamp: new Date(),
        };
        dispatch(addMessage(aiMessage));
      } else {
        dispatch(setError(err?.data?.message || 'Failed to send message. Please try again.'));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleReset = () => {
    dispatch(resetChat());
  };

  const handleDismissError = () => {
    dispatch(clearError());
  };

  const suggestionQueries = [
    {
      icon: TrendingUp,
      text: "Show me total orders by group leader",
      category: "Performance"
    },
    {
      icon: Users,
      text: "How many users do we have in total?",
      category: "Users"
    },
    {
      icon: BarChart3,
      text: "Which products are most popular?",
      category: "Products"
    }
  ];

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-secondary-50 via-white to-primary-50">
      {/* Enhanced Header */}
      <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-secondary-200 shadow-sm">
        <div className="flex items-center gap-3">
          <Logo size={40} />
          <div>
            <h1 className="text-xl font-semibold text-secondary-900">
              ChipChip AI Marketing Agent
            </h1>
            <p className="text-sm text-secondary-600">
              Ask questions about your marketing data and get insights
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          onClick={handleReset}
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          New Chat
        </Button>
      </header>

      {/* Messages Container */}
      <main
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-6 space-y-6"
      >
        {/* Enhanced Welcome Message */}
        {messages.length === 0 && !isLoading && (
          <div className="text-center py-16 max-w-4xl mx-auto">
            {/* <div className="relative mb-8">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center mx-auto shadow-lg">
                <MessageSquare className="w-10 h-10 text-primary-600" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-success-400 to-success-500 rounded-full flex items-center justify-center shadow-md">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
            </div> */}
            
            <h2 className="text-3xl font-bold text-secondary-900 mb-3">
              Welcome to ChipChip AI
            </h2>
            <p className="text-lg text-secondary-600 max-w-2xl mx-auto mb-8 leading-relaxed">
              Your intelligent marketing analytics companion. Ask me anything about user registrations, 
              orders, group leaders, or any insights you need to drive your business forward.
            </p>

            {/* Enhanced Suggestion Cards */}
            <div className="space-y-4">
              <p className="text-sm font-semibold text-secondary-700 uppercase tracking-wide">
                Try these popular queries
              </p>
              <div className="grid gap-4 md:grid-cols-3 max-w-3xl mx-auto">
                {suggestionQueries.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSend(suggestion.text)}
                    className="group p-4 bg-white/80 backdrop-blur-sm border border-secondary-200/50 rounded-xl hover:bg-white hover:border-primary-300 hover:shadow-lg transition-all duration-300 text-left"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-primary-100 to-primary-200 rounded-lg flex items-center justify-center group-hover:from-primary-200 group-hover:to-primary-300 transition-all duration-300">
                        <suggestion.icon className="w-4 h-4 text-primary-600" />
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-medium text-primary-600 mb-1">
                          {suggestion.category}
                        </div>
                        <div className="text-sm font-medium text-secondary-900 group-hover:text-primary-700 transition-colors">
                          {suggestion.text}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Feature highlights */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-gradient-to-br from-success-100 to-success-200 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-6 h-6 text-success-600" />
                </div>
                <h3 className="font-semibold text-secondary-900 mb-1">Real-time Analytics</h3>
                <p className="text-sm text-secondary-600">Get instant insights from your data</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-gradient-to-br from-warning-100 to-warning-200 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <BarChart3 className="w-6 h-6 text-warning-600" />
                </div>
                <h3 className="font-semibold text-secondary-900 mb-1">Smart Visualizations</h3>
                <p className="text-sm text-secondary-600">Automatic charts and graphs</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-gradient-to-br from-error-100 to-error-200 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <Users className="w-6 h-6 text-error-600" />
                </div>
                <h3 className="font-semibold text-secondary-900 mb-1">Customer Insights</h3>
                <p className="text-sm text-secondary-600">Understand your audience better</p>
              </div>
            </div>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="max-w-4xl mx-auto">
            <ErrorBanner
              message={error}
              onDismiss={handleDismissError}
            />
          </div>
        )}

        {/* Messages */}
        <div className="max-w-4xl mx-auto space-y-6">
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message}>
              {message.sender === 'ai' && message.data && (
                <div className="space-y-6 mt-6">
                  {/* Show chart if visualization is suggested */}
                  {message.data.visualization?.suggestion_needed && (
                    <ChartRenderer
                      type={message.data.visualization.chart_type}
                      data={message.data.visualization.data}
                      title={message.data.visualization.title}
                      className="shadow-lg border-0"
                    />
                  )}
                  
                  {/* Show table for raw results */}
                  {message.data.results && message.data.results.length > 0 && (
                    <TableRenderer
                      data={message.data.results}
                      title="Query Results"
                      className="shadow-lg border-0"
                    />
                  )}

                  {/* Show SQL query in a code block */}
                  {message.data.sql_query && (
                    <div className="bg-gradient-to-br from-secondary-900 to-secondary-800 text-secondary-100 p-6 rounded-xl shadow-lg border border-secondary-700/50">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-3 h-3 bg-success-400 rounded-full"></div>
                        <div className="w-3 h-3 bg-warning-400 rounded-full"></div>
                        <div className="w-3 h-3 bg-error-400 rounded-full"></div>
                        <span className="text-xs font-medium text-secondary-400 ml-2">
                          Generated SQL Query
                        </span>
                      </div>
                      <pre className="text-sm font-mono overflow-x-auto leading-relaxed">
                        <code className="text-secondary-100">{message.data.sql_query}</code>
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </ChatMessage>
          ))}
        </div>

        {/* Enhanced Loading Indicator */}
        {isLoading && (
          <div className="flex justify-center">
            <div className="flex items-center gap-4 bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-lg border border-secondary-200/50">
              <div className="relative">
                <LoadingSpinner size="sm" />
                <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-full animate-pulse opacity-20"></div>
              </div>
              <div>
                <span className="text-sm font-medium text-secondary-900">
                  Analyzing your query...
                </span>
                <div className="text-xs text-secondary-600 mt-1">
                  This may take a few moments
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={chatBottomRef} />
      </main>

      {/* Enhanced Chat Input */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/95 to-transparent pointer-events-none"></div>
        <div className="relative bg-white/80 backdrop-blur-sm border-t border-secondary-200/50">
          <ChatInput onSend={handleSend} loading={isLoading} />
        </div>
      </div>
    </div>
  );
};